import { useState } from "react";
import { useEmailCode, useSubmitEmail } from "@/features/email";
import { useOnboarding } from "@/features/onboarding";
import { BotMessage } from "../../components/BotMessage";
import { TurnMessages } from "../../components/TurnMessages";
import { QuickReplies } from "../../components/QuickReplies";
import { ChatComposer } from "../../components/ChatComposer";
import { ChatTurnLayout } from "../../components/ChatTurnLayout";
import { useTurnMessages } from "../../hooks/useTurnMessages";
import { formatSeconds, useCountdown } from "../../hooks/useCountdown";
import { chatCopy } from "../../engine/copy";
import type { ChatTurnProps } from "../../types/chat";

const CODE_LENGTH = 6;

const CodeReply = { Resend: "resend", ChangeEmail: "change-email" } as const;

export function EmailTurn({
  facts,
  profile,
  isPromptTyped,
  onAnswered,
}: ChatTurnProps) {
  const isCodeAsked = facts.isEmailSaved && !!profile.email;
  // Back from a reload with the email saved: continue at the code.
  const { messages, addUser, addBot, addError } = useTurnMessages(
    isCodeAsked
      ? [
          { from: "user", text: profile.email! },
          { from: "bot", lines: chatCopy.emailCode },
        ]
      : [],
  );

  const { getOnboardingState } = useOnboarding();
  const { submitEmail, isLoading: isSavingEmail } = useSubmitEmail({
    onError: addError,
  });
  const {
    confirmCode,
    resendCode,
    resendDelay,
    isLoading: isCheckingCode,
  } = useEmailCode({ onError: addError });

  const [isAskingCode, setIsAskingCode] = useState(isCodeAsked);
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  // Bumped on every code sent, restarting the resend countdown.
  const [codesSent, setCodesSent] = useState(0);
  const secondsLeft = useCountdown(resendDelay, codesSent);

  const handleSendEmail = async () => {
    const value = email.trim();
    addUser(value);
    setEmail("");

    if (!(await submitEmail(value))) {
      return;
    }

    await getOnboardingState();
    addBot(chatCopy.emailCode);
    setIsAskingCode(true);
    setCodesSent((count) => count + 1);
  };

  const handleSendCode = async (value: string) => {
    addUser(
      value.length === CODE_LENGTH
        ? `${value.slice(0, 3)}-${value.slice(3)}`
        : value,
    );
    setCode("");

    if (await confirmCode(value)) {
      onAnswered();
    }
  };

  const handleCodeChange = (value: string) =>
    setCode(value.replace(/\D/g, "").slice(0, CODE_LENGTH));

  const handleReply = async (reply: string) => {
    if (reply === CodeReply.ChangeEmail) {
      addUser("Schimbă emailul");
      addBot(chatCopy.email);
      setEmail(profile.email ?? "");
      setIsAskingCode(false);
      return;
    }

    addUser("Retrimite codul");
    if (await resendCode()) {
      addBot(chatCopy.emailCodeResent);
      setCodesSent((count) => count + 1);
    }
  };

  const canResend = secondsLeft === 0;

  return (
    <ChatTurnLayout
      messageCount={messages.length}
      thread={
        <>
          <BotMessage lines={chatCopy.email} isTyped={isPromptTyped} />
          <TurnMessages messages={messages} />
        </>
      }
      actions={
        isAskingCode ? (
          <>
            <QuickReplies
              disabled={isCheckingCode}
              onSelect={(reply) => void handleReply(reply)}
              replies={[
                {
                  id: CodeReply.Resend,
                  label: canResend
                    ? "Retrimite codul"
                    : `Retrimite codul (${formatSeconds(secondsLeft)})`,
                  disabled: !canResend,
                },
                { id: CodeReply.ChangeEmail, label: "Schimbă emailul" },
              ]}
            />
            <ChatComposer
              label="Codul din email"
              placeholder="Scrie codul din email"
              inputMode="numeric"
              autoComplete="one-time-code"
              enterKeyHint="send"
              maxLength={CODE_LENGTH}
              value={code}
              onChange={handleCodeChange}
              onSubmit={() => void handleSendCode(code)}
              isLoading={isCheckingCode}
            />
          </>
        ) : (
          <ChatComposer
            label="Email"
            placeholder="Scrie adresa ta de email"
            type="email"
            inputMode="email"
            autoComplete="email"
            autoCapitalize="none"
            enterKeyHint="send"
            value={email}
            onChange={setEmail}
            onSubmit={() => void handleSendEmail()}
            isLoading={isSavingEmail}
          />
        )
      }
    />
  );
}
